import { z } from 'zod'

// ============================================================================
// Cloud Function Request/Response Types (Live Dashboard)
// ============================================================================

export const GetSurveyQualityReportInput = z.object({
  siteIds: z.array(z.string()).nullish(),
})
export type GetSurveyQualityReportInput = z.infer<
  typeof GetSurveyQualityReportInput
>

export const SurveyQualityUnitResult = z.object({
  unitId: z.string(),
  siteId: z.string(),
  siteName: z.string(),
  ahjId: z.string().nullable(),
  elevatorType: z.string().nullable(),
  buildingDesignation: z.string().nullable(),
  hasSurvey: z.boolean(),
  surveyId: z.string().nullable(),
  submittedAt: z.string().nullable(),
  missingFields: z.array(
    z.object({
      field: z.string(),
      label: z.string(),
      category: z.string(),
    }),
  ),
  totalRequiredFields: z.number(),
  completedFields: z.number(),
  completionPercentage: z.number(),
  /** Denormalized from the unit — used by clients to gate modify-survey UI. */
  workRouteIds: z.array(z.string()).default([]),
})
export type SurveyQualityUnitResult = z.infer<typeof SurveyQualityUnitResult>

export const SurveyQualitySiteSummary = z.object({
  siteId: z.string(),
  siteName: z.string(),
  /** Active (non-archived) units only. */
  totalUnits: z.number(),
  /** Archived units on this site — excluded from survey-quality analysis. */
  totalArchivedUnits: z.number().default(0),
  unitsWithIssues: z.number(),
  unitsWithoutSurvey: z.number(),
  averageCompletionPercentage: z.number(),
  units: z.array(SurveyQualityUnitResult),
})
export type SurveyQualitySiteSummary = z.infer<typeof SurveyQualitySiteSummary>

export const SurveyQualityReport = z.object({
  totalSites: z.number(),
  /** Active (non-archived) units across all reported sites. */
  totalUnits: z.number(),
  /** Archived units across all sites — counted but not analyzed. */
  totalArchivedUnits: z.number().default(0),
  totalUnitsWithIssues: z.number(),
  totalUnitsWithoutSurvey: z.number(),
  averageCompletionPercentage: z.number(),
  sites: z.array(SurveyQualitySiteSummary),
  generatedAt: z.string(),
})
export type SurveyQualityReport = z.infer<typeof SurveyQualityReport>

// ============================================================================
// Report Template Types (Stored Reports)
// ============================================================================

export const SurveyQualityMissingQuestion = z.object({
  field: z.string(),
  questionText: z.string(),
  options: z.array(z.string()),
})
export type SurveyQualityMissingQuestion = z.infer<
  typeof SurveyQualityMissingQuestion
>

export const SurveyQualityUnitData = z.object({
  unitId: z.string(),
  unitName: z.string(),
  siteId: z.string(),
  siteName: z.string(),
  lastSurveyDate: z.string().nullable(),
  hasSurvey: z.boolean(),
  missingQuestions: z.array(SurveyQualityMissingQuestion),
})
export type SurveyQualityUnitData = z.infer<typeof SurveyQualityUnitData>

export const SurveyQualitySiteData = z.object({
  siteId: z.string(),
  siteName: z.string(),
  units: z.array(SurveyQualityUnitData),
})
export type SurveyQualitySiteData = z.infer<typeof SurveyQualitySiteData>

export const SurveyQualitySummary = z.object({
  /** Number of sites that have at least one outstanding issue (listed below). */
  totalSites: z.number(),
  /** Active (non-archived) sites in scope. */
  totalActiveSites: z.number().default(0),
  /** Archived sites in scope. */
  totalArchivedSites: z.number().default(0),
  /** Active (non-archived) units on the sites included in this report. */
  totalUnits: z.number(),
  /** Archived units across the company — counted but not analyzed. */
  totalArchivedUnits: z.number().default(0),
  totalUnitsWithIssues: z.number(),
  totalUnitsWithoutSurvey: z.number(),
  generatedAt: z.string(),
})
export type SurveyQualitySummary = z.infer<typeof SurveyQualitySummary>

export const SurveyQualityReportData = z.object({
  summary: SurveyQualitySummary,
  sites: z.array(SurveyQualitySiteData),
})
export type SurveyQualityReportData = z.infer<typeof SurveyQualityReportData>

/**
 * Type guard to check if report data is SurveyQualityReportData
 */
export function isSurveyQualityReportData(
  data: unknown,
): data is SurveyQualityReportData {
  return SurveyQualityReportData.safeParse(data).success
}
